import { colors } from '@creditornot/cb-ingredients';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getBasketSize } from 'apis/merchant-gateway/insights';
import { BasketSizeData, CustomerType } from 'apis/merchant-gateway/insights/avg-basket-size/types';
import { HorizontalBarChart, LegendChartType } from 'modules/experience-core';

export type BasketSizeChartProps = {
  data: BasketSizeData<null, CustomerType> | null | undefined;
  basketValueType: 'items' | 'value';
  currency?: Currency;
  summaryKey: string;
  isVATEnabled: boolean;
};

const BasketSizeChart = ({ data, summaryKey, currency, basketValueType, isVATEnabled }: BasketSizeChartProps) => {
  const { formatMessage } = useIntl();

  const chartLegends = {
    [CustomerType.RETURNING]: {
      value: formatMessage({ id: 'insights.customer-base.returning' }),
      color: colors.wolt88,
      type: LegendChartType.BAR,
    },
    [CustomerType.NEW]: {
      value: formatMessage({ id: 'insights.customer-base.new' }),
      color: colors.wolt32,
      type: LegendChartType.BAR,
    },
  };

  const chartData = useMemo(() => {
    const initialValue = {
      name: '',
      summaryValue:
        basketValueType === 'value' ? getBasketSize(data?.total, isVATEnabled) : data?.total.avgBasketSizeItems,
      [CustomerType.RETURNING]: 0,
      [CustomerType.NEW]: 0,
    };

    return (data?.split ?? []).reduce((acc, item) => {
      if (!item.label) {
        return acc;
      }

      const prevSize = acc[item.label];

      return {
        ...acc,
        [item.label]:
          prevSize +
          (basketValueType === 'value' ? getBasketSize(item.total, isVATEnabled) : item.total.avgBasketSizeItems),
      };
    }, initialValue);
  }, [basketValueType, data?.split, data?.total, isVATEnabled]);

  return (
    <HorizontalBarChart
      legends={chartLegends}
      isStacked={false}
      data={[chartData]}
      summaryKey={summaryKey}
      currency={currency}
    />
  );
};

export default BasketSizeChart;
